import { Injectable } from '@angular/core';
import { HIDE_LOCAL_IP_REGEX } from '@app/app.constants';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { ClientLoggingConfig } from '@app/core/interfaces/c5/client-logging-config';
import { Device, DeviceKind, toDeviceKind } from '@app/core/interfaces/c5/device';
import { DevicePushTokenScope } from '@app/core/interfaces/c5/device-push-token';
import { DeviceSpecification, DeviceSpecificationType } from '@app/core/interfaces/c5/device-specification';
import { PhoneNumber } from '@app/core/interfaces/c5/phone-number';
import { PhoneNumberDirection } from '@app/core/interfaces/c5/phone-number-direction';
import { PhoneNumberType } from '@app/core/interfaces/c5/phone-number-type';
import { EndUserData } from '@app/core/interfaces/end-user-data';
import { EnhancedAllowedDevice } from '@app/core/interfaces/enhanced-allowed-device';
import { Registration } from '@app/core/models/registration';
import { isServiceProvider } from '@app/core/models/user-level';
import { PhoneNumberPipe } from '@app/shared/pipes/phone-number.pipe';
import { sortBy, toMap } from '@app/utils/common';
import { Logger } from '@app/utils/logger';
import { StringMap } from '@app/utils/string-map';
import { FeatureStatus } from '@business/feature/interfaces/feature-status';
import { Features } from '@business/feature/interfaces/features';
import { fromUnixTime, isAfter } from 'date-fns';
import { ClientLoggingConfigViewModel, EnhancedDevice, EnhancedPhoneNumber } from '.';
import { RegistrationStatus } from '../services/registration-status-utils.service';

@Injectable({
  providedIn: 'root',
})
export class DeviceEnhancerService {
  user: EndUserData;

  constructor(
    private authenticationService: AuthenticationService,
    private phoneNumberPipe: PhoneNumberPipe
  ) {}

  assignableNumbersGrouped(device: EnhancedDevice, endUser: EndUserData) {
    /*
    doesNotHaveIt = (phoneNum, numbers) ->
      result = (pn for pn in numbers when pn.id == phoneNum.id and pn.dir != 'OUT')
      result.length == 0

    internalNums = []; fixedNetworkNums = []; mobileNums = []
    for phoneNum in listContextUserAssignableNumbers() when phoneNum.dir != 'OUT'
      switch phoneNum.type
        when 'INTERNAL' then if doesNotHaveIt(phoneNum, device._phoneNumsGrouped.internalNums) then internalNums.push(phoneNum)
        when 'FIXED_NETWORK' then if doesNotHaveIt(phoneNum, device._phoneNumsGrouped.fixedNetworkNums) then fixedNetworkNums.push(phoneNum)
        when 'MSISDN', 'CORPORATE' then if doesNotHaveIt(phoneNum, device._phoneNumsGrouped.mobileNums) then mobileNums.push(phoneNum)
        else $log.log('ERROR: Could not find correct group for ', phoneNum.type, ' for phone number ', phoneNum)
    device._assignableNumsGrouped = {
      internalNums: internalNums.sort (a,b) -> Helper.sortBy('_name', a, b)
      fixedNetworkNums: fixedNetworkNums.sort (a,b) -> Helper.sortBy('_name', a, b)
      mobileNums: mobileNums.sort (a,b) -> Helper.sortBy('_name', a, b)
    }
    device._assignableNumsGrouped.isEmpty = (internalNums.length == 0) and (fixedNetworkNums.length == 0) and (mobileNums.length == 0)
    device
    */
    const doesNotHaveIt = (phoneNumber: EnhancedPhoneNumber, phoneNumbers: EnhancedPhoneNumber[]) =>
      phoneNumbers.filter((value) => value.id === phoneNumber.id && value.dir !== PhoneNumberDirection.OUT)
        .length === 0;

    const fixedNetworkNumbers: EnhancedPhoneNumber[] = [];
    const internalNumbers: EnhancedPhoneNumber[] = [];
    const mobileNumbers: EnhancedPhoneNumber[] = [];

    const assignableNumbers = this.listContextUserAssignableNumbers(endUser);

    assignableNumbers
      .filter((phoneNumber) => phoneNumber.dir !== PhoneNumberDirection.OUT)
      .forEach((phoneNumber) => {
        switch (phoneNumber.type) {
          case PhoneNumberType.INTERNAL:
            if (doesNotHaveIt(phoneNumber, device.phoneNumbersGrouped.internalNumbers)) {
              internalNumbers.push(phoneNumber);
            }

            break;
          case PhoneNumberType.FIXED_NETWORK:
            if (doesNotHaveIt(phoneNumber, device.phoneNumbersGrouped.fixedNetworkNumbers)) {
              fixedNetworkNumbers.push(phoneNumber);
            }

            break;
          case PhoneNumberType.CORPORATE:
          case PhoneNumberType.MSISDN:
            if (doesNotHaveIt(phoneNumber, device.phoneNumbersGrouped.mobileNumbers)) {
              mobileNumbers.push(phoneNumber);
            }

            break;
          default:
            Logger.error(
              `ERROR: Couldn't find correct group for ${phoneNumber.type} for phone number ${phoneNumber.name}`
            );
        }
      });

    return {
      fixedNetworkNumbers,
      internalNumbers,
      isEmpty: fixedNetworkNumbers.length === 0 && internalNumbers.length === 0 && mobileNumbers.length === 0,
      mobileNumbers,
    };
  }

  enhance(
    device: Device,
    allowedDevices: EnhancedAllowedDevice[],
    deviceSpecifications: DeviceSpecification[],
    endUser: EndUserData,
    registrations: Registration[],
    features: Features,
    clientLogConfig: ClientLoggingConfig[]
  ): EnhancedDevice {
    const allowedDevicesMap = toMap(allowedDevices, 'deviceSpecId');
    const deviceSpecificationsMap = toMap(deviceSpecifications, 'id');

    const identities = this.listIdentities(endUser);
    const externalIdentity = this.enhancePhoneNumber(
      this.findIdentity(device.phoneNumbers, identities.external, 'external')
    );

    const internalIdentity = this.enhancePhoneNumber(endUser.user._mainInternalNumber);
    const identitiesSms = this.listIdentitiesSms(identities.external, internalIdentity);
    const deviceKind = toDeviceKind(device);

    let enhancedDevice: EnhancedDevice = {
      ...device,
      canEditClientLoggingConfig:
        deviceKind === DeviceKind.DESKTOP_CONTROL || deviceKind === DeviceKind.WEB_CONTROL,
      clientLoggingConfig: this.getClientLogConfig(clientLogConfig, deviceKind),
      kind: deviceKind,
      identity: {
        external: externalIdentity,
        internal: internalIdentity,
        sms: this.findIdentity(device.phoneNumbers, identitiesSms, 'sms'),
      },
      identities: {
        ...identities,
        sms: identitiesSms,
      },
      isPhoneNumberAssignmentAllowed: phoneNumberAssignmentAllowed(deviceKind, features),
    };
    if (device.excludeIpFilter == null) {
      enhancedDevice.excludeIpFilter = false;
    }
    if (device.phone) {
      const deviceSpecId = device.phone.deviceSpecId;
      const deviceSpec = deviceSpecificationsMap[deviceSpecId];
      let allowedDevice = allowedDevicesMap[deviceSpecId];

      if (allowedDevice === undefined) {
        Logger.warn(
          `Warning! Device ${deviceSpecId} not found in allowed devices, generating placeholder from device spec`
        );

        allowedDevice = this.generateFromDeviceSpec(
          deviceSpecId,
          deviceSpec,
          allowedDevicesMap,
          deviceSpecificationsMap
        );

        allowedDevices.push(allowedDevice);
      }

      enhancedDevice = {
        ...enhancedDevice,
        ...this.fromAllowedDevice(device, allowedDevice),
        deviceSpec: deviceSpec,
      };
    } else if (enhancedDevice.kind == DeviceKind.PHONE || enhancedDevice.kind == DeviceKind.IAD) {
      // For invalid phones assume no support and no functionality
      // and add dummy phone to avoid undefined data access (see #45468)
      Logger.warn(`Missing phone section for device ${device.name} (${device.id}). Using dummy data`);
      enhancedDevice = {
        ...enhancedDevice,
        isUnsupported: true,
        phone: {
          domainId: device.siteId,
          enterpriseId: device.enterpriseId,
          funcKeys: [],
        },
      };
    }

    // device registration infos
    enhancedDevice.canBeRegistered = true;
    switch (enhancedDevice.kind) {
      case DeviceKind.WEB_CONTROL:
        enhancedDevice.isCommunicator =
          features.webControl.status === FeatureStatus.ACTIVE &&
          features.webControl.webControlData?.webCommunicator === true;
        break;
      case DeviceKind.MOBILE_CONTROL:
        enhancedDevice.isCommunicator =
          features.mobileControl.status === FeatureStatus.ACTIVE &&
          features.mobileControl.mobileControlData?.mobileCommunicator === true;
        break;
      case DeviceKind.DESKTOP_CONTROL:
        enhancedDevice.isCommunicator =
          features.desktopControl.status === FeatureStatus.ACTIVE &&
          features.desktopControl.desktopControlData?.desktopCommunicator === true;
        break;

      case DeviceKind.MOBILE:
      case DeviceKind.MS_TEAMS:
      case DeviceKind.EXTERNAL_PBX:
        enhancedDevice.canBeRegistered = false;
        break;

      default:
        break;
    }

    if (enhancedDevice.canBeRegistered === true) {
      enhancedDevice.registrations = registrations
        .filter((registration) => registration.deviceId === device.id)
        .map((registration) => {
          if (HIDE_LOCAL_IP_REGEX.test(registration.userAgent)) {
            registration.registeredIp = null;
          }

          return registration;
        });
      enhancedDevice.hasRegistrations = enhancedDevice.registrations.length > 0;
      enhancedDevice.hasBlockedRegistrations = enhancedDevice.registrations.some(
        (x) => x.status === 'blocked'
      );
      let hasCallPushTokens = enhancedDevice.pushTokens?.some((x) =>
        x.scope.some((scope) => scope === DevicePushTokenScope.CALL)
      );
      if (enhancedDevice.hasBlockedRegistrations) {
        enhancedDevice.registrationStatus = RegistrationStatus.Blocked;
      } else if (enhancedDevice.hasRegistrations) {
        enhancedDevice.registrationStatus = RegistrationStatus.Registered;
      } else if (enhancedDevice.kind === DeviceKind.MOBILE_CONTROL && hasCallPushTokens) {
        enhancedDevice.registrationStatus = RegistrationStatus.PushRegistered;
      } else if (enhancedDevice.registrations != null) {
        enhancedDevice.registrationStatus = RegistrationStatus.Unregistered;
      } else {
        enhancedDevice.registrationStatus = RegistrationStatus.Unknown;
      }
    } else {
      enhancedDevice.registrationStatus = RegistrationStatus.None;
    }

    enhancedDevice.filteredPhoneNumbersBasedOnAuthUserRole =
      this.filterPhoneNumbersBasedOnAuthUserRole(enhancedDevice);
    enhancedDevice.phoneNumbersGrouped = this.groupPhoneNumbers(enhancedDevice);
    enhancedDevice.assignableNumbersGrouped = this.assignableNumbersGrouped(enhancedDevice, endUser);

    if (enhancedDevice.kind === DeviceKind.GENERIC) {
      const [sipUsername, sipDomain] = enhancedDevice.sipUri.split('@');
      enhancedDevice.sipUsername = sipUsername;
      enhancedDevice.sipDomain = sipDomain;
    }

    return enhancedDevice;
  }

  enhancePhoneNumber(phoneNumber: PhoneNumber): EnhancedPhoneNumber | null {
    if (phoneNumber == null) {
      return null;
    }

    return {
      ...phoneNumber,
      name: this.phoneNumberPipe.transform(phoneNumber),
    };
  }

  filterPhoneNumbersBasedOnAuthUserRole(device: Device) {
    let value;

    if (isServiceProvider(this.authenticationService.authUser)) {
      value = device.phoneNumbers;
    } else {
      value = device.phoneNumbers.filter(
        (phoneNumber) =>
          phoneNumber.dir === PhoneNumberDirection.IN || phoneNumber.dir === PhoneNumberDirection.IN_OUT
      );
    }

    return value;
  }

  findDeviceName(
    deviceSpec: DeviceSpecification,
    allowedDevicesMap: StringMap<EnhancedAllowedDevice>,
    deviceSpecsMap: StringMap<DeviceSpecification>
  ): string {
    let deviceName = '';

    if (deviceSpec !== undefined) {
      const allowedDevices = Object.values(allowedDevicesMap);
      let manufacturer = 'Unknown';

      if (deviceSpec.manufacturer && deviceSpec.manufacturer.length > 0) {
        // Uppercase the first letter
        manufacturer = deviceSpec.manufacturer.charAt(0).toUpperCase() + deviceSpec.manufacturer.slice(1);
      }

      deviceName = `${manufacturer} ${deviceSpec.type}`;

      Logger.debug(`findDeviceName: generated ${deviceName}, trying to find improved name`);

      for (let allowedDevice of allowedDevices) {
        const ds = deviceSpecsMap[allowedDevice.deviceSpecId];

        if (
          ds !== undefined &&
          ds.manufacturer === deviceSpec.manufacturer &&
          ds.series === deviceSpec.series &&
          ds.type === deviceSpec.type
        ) {
          Logger.debug('Found allowedDevice with matching device spec', allowedDevice);
          deviceName = allowedDevice.deviceName;
          break;
        }
      }

      Logger.debug(`return deviceName ${deviceName} for deviceSpec id ${deviceSpec.id}`);
    }

    return deviceName;
  }

  findIdentity(phoneNumbers: PhoneNumber[], identities: PhoneNumber[], kind) {
    let phoneNumber: PhoneNumber | null = null;
    const correctDirection = (phoneNumber) =>
      phoneNumber.dir === PhoneNumberDirection.OUT || phoneNumber.dir === PhoneNumberDirection.IN_OUT;
    const sortByUserProvided = (a: PhoneNumber, b: PhoneNumber) => (a.netNumber > b.netNumber ? 1 : -1);

    switch (kind) {
      case 'internal':
        phoneNumbers = phoneNumbers.filter(
          (phoneNumber) => phoneNumber.type === PhoneNumberType.INTERNAL && correctDirection(phoneNumber)
        );
        break;
      case 'external':
        phoneNumbers = phoneNumbers.filter(
          (phoneNumber) => phoneNumber.type !== PhoneNumberType.INTERNAL && correctDirection(phoneNumber)
        );
        break;
      case 'sms':
        phoneNumbers = phoneNumbers.filter(
          (phoneNumber) =>
            (phoneNumber.type === PhoneNumberType.MSISDN || phoneNumber.type === PhoneNumberType.CORPORATE) &&
            phoneNumber.smsMo === true
        );
        break;
      default:
        phoneNumbers = [];
        break;
    }

    if (phoneNumbers.length > 0) {
      phoneNumbers = phoneNumbers
        .sort(sortByUserProvided)
        .filter((phoneNumber) => phoneNumber.id === phoneNumbers[0].id);
      phoneNumber = phoneNumbers.length > 0 ? phoneNumbers[0] : undefined;
    }

    return phoneNumber;
  }

  fromAllowedDevice(device: Device, allowedDevice: EnhancedAllowedDevice) {
    return {
      displayName: allowedDevice.deviceName,
      firmware: allowedDevice.firmware,
      hasFunctionKeys: allowedDevice.hasFunctionKeys || false,
      isDeprecated: allowedDevice.deprecated,
      isLimitedSupport: allowedDevice.limited,
      isPreview: allowedDevice.preview,
      isUnsupported: allowedDevice.unsupported || false,
      phoneActions: {
        allowReboot: allowedDevice.allowReboot || false,
        allowLoadSettings: allowedDevice.allowLoadSettings || false,
        allowRecreateSettings: allowedDevice.allowRecreateSettings || false,
      },
    };
  }

  generateFromDeviceSpec(
    deviceSpecId: string,
    deviceSpec: DeviceSpecification,
    allowedDevices: StringMap<EnhancedAllowedDevice>,
    deviceSpecs: StringMap<DeviceSpecification>
  ): any {
    const newDeviceSpec = {
      allowReboot: false,
      allowLoadSettings: false,
      allowRecreateSettings: false,
      default: false,
      deprecated: false,
      unsupported: true,
      deviceName: this.findDeviceName(deviceSpec, allowedDevices, deviceSpecs),
      deviceSpecId: deviceSpecId,
      firmware: deviceSpec ? deviceSpec.softwareLoad : undefined,
      id: null,
      hasFunctionKeys: false,
      type: DeviceSpecificationType.PHONE,
    };

    deviceSpecs[deviceSpecId] = newDeviceSpec;

    return newDeviceSpec;
  }

  groupPhoneNumbers(device: EnhancedDevice) {
    const fixedNetworkNumbers: EnhancedPhoneNumber[] = [];
    const internalNumbers: EnhancedPhoneNumber[] = [];
    const mobileNumbers: EnhancedPhoneNumber[] = [];

    for (let phoneNumber of device.filteredPhoneNumbersBasedOnAuthUserRole) {
      phoneNumber = this.enhancePhoneNumber(phoneNumber);

      switch (phoneNumber.type) {
        case PhoneNumberType.INTERNAL:
          internalNumbers.push(phoneNumber);
          break;
        case PhoneNumberType.FIXED_NETWORK:
          fixedNetworkNumbers.push(phoneNumber);
          break;
        case PhoneNumberType.CORPORATE:
        case PhoneNumberType.MSISDN:
          mobileNumbers.push(phoneNumber);
          break;
        default:
          Logger.error(`Couldn't find correct group for ${phoneNumber.type} for phone number`, phoneNumber);
      }
    }

    return {
      fixedNetworkNumbers: fixedNetworkNumbers.sort(sortBy('name')),
      internalNumbers: internalNumbers.sort(sortBy('name')),
      isEmpty: fixedNetworkNumbers.length === 0 && internalNumbers.length === 0 && mobileNumbers.length === 0,
      mobileNumbers: mobileNumbers.sort(sortBy('name')),
    };
  }

  listContextUserAssignableNumbers(endUser: EndUserData): EnhancedPhoneNumber[] {
    const enduserAssignableNumbers = [];

    for (let phoneNumber of endUser.user.phoneNumbers) {
      const hidden = phoneNumber.hide === false;
      const unknownServiceType = phoneNumber.serviceType === 'UNKNOWN';
      const inOrOut =
        phoneNumber.dir === PhoneNumberDirection.IN || phoneNumber.dir === PhoneNumberDirection.IN_OUT;
      const internal =
        phoneNumber.type === PhoneNumberType.INTERNAL ||
        (phoneNumber.screenNumId !== undefined && phoneNumber.screenNumId > 0);

      if (hidden && unknownServiceType && inOrOut && internal) {
        enduserAssignableNumbers.push(this.enhancePhoneNumber(Object.assign({}, phoneNumber)));
      }
    }

    enduserAssignableNumbers.sort(sortBy('_name'));

    // for phoneNum in controller.endUser.phoneNumbers
    //       if phoneNum.hide == false and phoneNum.serviceType == 'UNKNOWN' and
    //          (phoneNum.dir == 'IN' or phoneNum.dir == 'IN_OUT') and
    //          (phoneNum.type == 'INTERNAL' || (phoneNum.screenNumId? && phoneNum.screenNumId > 0))
    //         phoneNum._name = $filter('phoneNumber')(phoneNum)
    //         endUserAssignableNumbers.push(phoneNum)
    //     endUserAssignableNumbers.sort (a,b) -> Helper.sortBy('_name', a, b)

    return enduserAssignableNumbers;
  }

  listIdentities(endUser: EndUserData) {
    const endUserIdentities = {
      external: [],
      internal: [],
      sms: [],
    };

    endUser.user.phoneNumbers.forEach((phoneNumber: PhoneNumber) => {
      const visible = phoneNumber.hide === false;
      const unknownServiceType = phoneNumber.serviceType === 'UNKNOWN';
      const direction = phoneNumber.dir === 'OUT' || phoneNumber.dir === 'IN_OUT';

      if (visible && unknownServiceType && direction) {
        const enhancedPhoneNumber: EnhancedPhoneNumber = this.enhancePhoneNumber(phoneNumber);

        if (phoneNumber.type === 'INTERNAL') {
          endUserIdentities.internal.push(enhancedPhoneNumber);
        } else {
          endUserIdentities.external.push(enhancedPhoneNumber);
        }
      }
    });

    return endUserIdentities;
  }

  listIdentitiesSms(externalIdentities: EnhancedPhoneNumber[], internalIdentity: EnhancedPhoneNumber) {
    return externalIdentities.filter(
      (phoneNumber) =>
        phoneNumber.type === PhoneNumberType.MSISDN ||
        (phoneNumber.type === PhoneNumberType.CORPORATE && phoneNumber.internal === internalIdentity.internal)
    );
  }

  private getClientLogConfig(
    clientLogConfig: ClientLoggingConfig[],
    deviceKind: string
  ): ClientLoggingConfigViewModel | null {
    const maybeConfig = clientLogConfig.find((config) => config.application === deviceKind);

    if (maybeConfig != null) {
      const { activeUntil, application, id, level } = maybeConfig;
      const activeUntilDate = activeUntil != null ? fromUnixTime(activeUntil / 1000) : null;

      return {
        active: level !== 'OFF',
        activeUntil: activeUntilDate,
        activeUntilAfterNow: activeUntil != null && isAfter(activeUntilDate, new Date()),
        application: application,
        id: id,
        level: level,
      };
    }

    return null;
  }
}

function phoneNumberAssignmentAllowed(deviceKind: DeviceKind, features: Features): boolean {
  let isAllowed = false;
  switch (deviceKind) {
    case DeviceKind.DESKTOP_CONTROL:
      if (features.desktopControl != undefined && features.desktopControl.desktopControlData != undefined) {
        const data = features.desktopControl.desktopControlData;
        if (data.desktopCommunicator === true || data.desktopCti === true) {
          isAllowed = true;
        }
      }
      break;
    case DeviceKind.WEB_CONTROL:
      if (features.webControl != undefined && features.webControl.webControlData != undefined) {
        const data = features.webControl.webControlData;
        if (data.webCommunicator === true) {
          isAllowed = true;
        }
      }
      break;

    case DeviceKind.MOBILE_CONTROL:
      if (features.mobileControl != undefined && features.mobileControl.mobileControlData != undefined) {
        const data = features.mobileControl.mobileControlData;
        if (
          data.mobileCommunicator === true ||
          data.mobileCommunicatorWithoutGsm === true ||
          data.mobileOffice === true
        ) {
          isAllowed = true;
        }
      }
      break;

    default:
      isAllowed = true;
      break;
  }

  return isAllowed;
}
